import { useMemo } from 'react';

import { Money, formatMoney } from '@betterment/core';
import { Box, H6, Separator, Text, ToggleTip } from '@betterment/samba-react';

const hasNonZeroCents = (amount?: Money) => {
  return amount && amount.cents !== 0;
};

type TotalEarnedProps = {
  totalEarned?: Money;
  marketChanges?: Money;
  dividendsReinvested?: Money;
  dividendsPending?: Money;
  interestPaid?: Money;
  managementFees?: Money;
};

const toggleTipMsg = `How your net deposits changed from market volatility, dividends, interest, and fees.`;

export const TotalEarned: React.FC<TotalEarnedProps> = ({
  totalEarned,
  marketChanges,
  dividendsReinvested,
  dividendsPending,
  interestPaid,
  managementFees,
}) => {
  const willShowSection = useMemo(
    () =>
      hasNonZeroCents(totalEarned) ||
      hasNonZeroCents(marketChanges) ||
      hasNonZeroCents(dividendsReinvested) ||
      hasNonZeroCents(dividendsPending) ||
      hasNonZeroCents(interestPaid) ||
      hasNonZeroCents(managementFees),
    [totalEarned, marketChanges, dividendsReinvested, dividendsPending, interestPaid, managementFees]
  );

  if (!willShowSection) {
    return null;
  }

  return (
    <Box>
      {totalEarned && (
        <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box css={{ display: 'grid' }}>
            <Box css={{ display: 'flex' }}>
              <H6 variant="title100" css={{ fontWeight: 'normal' }}>
                Earned
              </H6>
              <ToggleTip>
                <ToggleTip.Trigger iconName="InfoCircle" label="Toggle Tip" />
                <ToggleTip.Content side="right" css={{ maxWidth: '300px' }}>
                  {toggleTipMsg}
                </ToggleTip.Content>
              </ToggleTip>
            </Box>
          </Box>
          <Box>
            <Text>{formatMoney(totalEarned)}</Text>
          </Box>
        </Box>
      )}
      {marketChanges && marketChanges.cents !== 0 && (
        <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Text color="text.weak">Market changes</Text>
          </Box>
          <Box>
            <Text color="text.weak">{formatMoney(marketChanges)}</Text>
          </Box>
        </Box>
      )}
      {dividendsReinvested && dividendsReinvested.cents !== 0 && (
        <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Text color="text.weak">Dividends earned</Text>
          </Box>
          <Box>
            <Text color="text.weak">{formatMoney(dividendsReinvested)}</Text>
          </Box>
        </Box>
      )}
      {dividendsPending && dividendsPending.cents !== 0 && (
        <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Text color="text.weak">Pending dividends</Text>
          </Box>
          <Box>
            <Text color="text.weak">{formatMoney(dividendsPending)}</Text>
          </Box>
        </Box>
      )}
      {interestPaid && interestPaid.cents !== 0 && (
        <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Text color="text.weak">Interest earned</Text>
          </Box>
          <Box>
            <Text color="text.weak">{formatMoney(interestPaid)}</Text>
          </Box>
        </Box>
      )}
      {managementFees && managementFees.cents !== 0 && (
        <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Text color="text.weak">Management fees deducted</Text>
          </Box>
          <Box>
            <Text color="text.weak">{formatMoney(managementFees)}</Text>
          </Box>
        </Box>
      )}
      <Separator css={{ mt: 'space.400' }} />
    </Box>
  );
};
